import axios from "axios";
import { loginUrlFor } from './loginRedirect';

// Global axios interceptors — installed once at app startup (imported from index.js).
//
// Auth model:
//   - Access token (PC_USR_TOKEN): short-lived (1 day), sent as Bearer on every request.
//   - Refresh token (PC_USR_REFRESH_TOKEN): long-lived (6 days). When the access token expires and
//     a request returns 401, we transparently exchange the refresh token for a new access token
//     (PCRefreshToken.php), then retry the original request. If the refresh also fails, we clear
//     auth and send the user back to login.

const BASE_URL = process.env.REACT_APP_BASE_URL;
const REFRESH_URL = `${BASE_URL}services/query/PCRefreshToken.php`;

// Request: attach the Bearer access token when a caller hasn't set one explicitly.
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("PC_USR_TOKEN");
        if (token && config.headers && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Single-flight refresh: concurrent 401s share one refresh request instead of stampeding.
let refreshPromise = null;

function refreshAccessToken() {
    const refreshToken = localStorage.getItem("PC_USR_REFRESH_TOKEN");
    if (!refreshToken) {
        return Promise.reject(new Error("no refresh token"));
    }
    if (!refreshPromise) {
        refreshPromise = axios
            .post(REFRESH_URL, { REFRESH_TOKEN: refreshToken })
            .then((res) => {
                if (res.data && res.data.success === 1 && res.data.PC_USR_TOKEN) {
                    localStorage.setItem("PC_USR_TOKEN", res.data.PC_USR_TOKEN);
                    if (res.data.PC_USR_REFRESH_TOKEN) {
                        localStorage.setItem("PC_USR_REFRESH_TOKEN", res.data.PC_USR_REFRESH_TOKEN);
                    }
                    return res.data.PC_USR_TOKEN;
                }
                throw new Error("refresh rejected");
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
}

function forceLogout() {
    try {
        localStorage.clear();
    } catch (e) {
        // ignore storage errors
    }
    // Back to the login page, which returns the user to the page they were on (a record link,
    // say) after signing in — see helper/loginRedirect.js. Public pages are left alone.
    const here = window.location.pathname;
    if (/^\/(admin|report)\//.test(here)) {
        window.location.href = loginUrlFor(here + window.location.search);
    } else if (here !== "/" && here !== "/admin") {
        window.location.href = "/";
    }
}

// Response: on 401, try one transparent refresh + retry; otherwise (or if refresh fails) log out.
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config || {};
        const status = error?.response?.status;
        const url = original.url || "";
        const isAuthCall =
            url.indexOf("PCRefreshToken.php") !== -1 || url.indexOf("PCLogin.php") !== -1;

        if (status === 401 && !original._retry && !isAuthCall) {
            original._retry = true;
            try {
                const newToken = await refreshAccessToken();
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${newToken}`;
                return axios(original); // retry the original request with the fresh token
            } catch (e) {
                forceLogout();
                return Promise.reject(error);
            }
        }

        if (status === 401 && isAuthCall) {
            // The refresh token itself is invalid/expired → the session is genuinely over.
            forceLogout();
        }

        return Promise.reject(error);
    }
);

export default axios;
